import React from 'react';

type BadgeVariant = 'solid' | 'soft' | 'outline';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  color?:
    | 'primary'
    | 'accent'
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'neutral';
  variant?: BadgeVariant;
}

const colorMap: Record<
  NonNullable<BadgeProps['color']>,
  { bg: string; text: string; border?: string }
> = {
  primary: {
    bg: 'var(--color-primary-light)',
    text: 'var(--color-white)',
  },
  accent: {
    bg: 'var(--color-accent-light)',
    text: 'var(--color-gray-900)',
  },
  success: {
    bg: 'var(--color-success-light)',
    text: 'var(--color-success)',
  },
  warning: {
    bg: 'var(--color-warning-light)',
    text: 'var(--color-warning)',
  },
  error: {
    bg: 'var(--color-error-light)',
    text: 'var(--color-error)',
  },
  info: {
    bg: 'var(--color-info-light)',
    text: 'var(--color-info)',
  },
  neutral: {
    bg: 'var(--color-gray-100)',
    text: 'var(--color-gray-700)',
  },
};

export const Badge: React.FC<BadgeProps> = ({
  color = 'neutral',
  variant = 'soft',
  className,
  style,
  children,
  ...rest
}) => {
  const palette = colorMap[color];

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingInline: '0.5rem',
    paddingBlock: '0.125rem',
    borderRadius: '9999px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    borderWidth: 0,
  };

  let variantStyle: React.CSSProperties = {};

  if (variant === 'solid') {
    variantStyle = {
      backgroundColor: palette.bg,
      color: palette.text,
    };
  } else if (variant === 'outline') {
    variantStyle = {
      backgroundColor: 'transparent',
      color: palette.text,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: palette.text,
    };
  } else {
    // soft
    variantStyle = {
      backgroundColor: palette.bg,
      color: palette.text,
    };
  }

  return (
    <span
      className={className}
      style={{ ...baseStyle, ...variantStyle, ...style }}
      {...rest}
    >
      {children}
    </span>
  );
};

export default Badge;

